import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, ArrowRight, Loader2, Building2, Check } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import StickyCTA from "@/components/StickyCTA";
import Calculator from "@/components/Calculator";
import JsonLd from "@/components/JsonLd";
import { api, SITE_URL, IS_BOT } from "@/lib/api";
import { getTileConfig } from "@/lib/mapTiles";

const townIcon = L.divIcon({
  className: "",
  html: `<div style="width:14px;height:14px;background:#D4AF37;border:2px solid #0A0A0A;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 0 0 2px rgba(212,175,55,0.25)"></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 14],
  popupAnchor: [0, -14],
});

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const fmt = (n) => (n == null ? "–" : Math.round(n).toLocaleString("sk-SK"));

export default function OkresPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [tiles, setTiles] = useState(null);

  useEffect(() => { getTileConfig().then(setTiles); }, []);

  useEffect(() => {
    setState("loading");
    api.get(`/seo/okres/${slug}`)
      .then((r) => {
        setData(r.data);
        setState("ok");
        document.title = `Zateplenie okres ${r.data.name} – ceny, firmy a kalkulačka | Zateplenia.eu`;
        setMeta("description", `Zateplenie fasád v okrese ${r.data.name}: orientačné ceny od ${r.data.prices?.[0]?.price_per_m2 ?? "55"} €/m², ${r.data.town_count} obcí, overené firmy a online kalkulačka do 60 sekúnd.`);
        setCanonical(`${SITE_URL}/okres/${slug}`);
      })
      .catch(() => setState("404"));
  }, [slug]);

  if (state === "loading") {
    return <div className="min-h-screen bg-ink text-white flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }
  if (state === "404" || !data) {
    return (
      <div className="min-h-screen bg-ink text-white">
        <Header />
        <main className="pt-40 pb-24 text-center">
          <h1 className="font-display text-4xl uppercase mb-4">Okres nenájdený</h1>
          <Link to="/okresy" className="text-gold hover:underline">Zobraziť všetky okresy</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const townsWithGps = data.towns.filter((t) => t.lat && t.lng);
  const center = townsWithGps.length
    ? [townsWithGps.reduce((s, t) => s + t.lat, 0) / townsWithGps.length,
       townsWithGps.reduce((s, t) => s + t.lng, 0) / townsWithGps.length]
    : [48.72, 19.5];

  return (
    <div className="min-h-screen bg-ink text-white">
      <Header />
      <JsonLd data={{
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Domov", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Okresy", item: `${SITE_URL}/okresy` },
          { "@type": "ListItem", position: 3, name: `Okres ${data.name}`, item: `${SITE_URL}/okres/${slug}` },
        ],
      }} />

      <main className="pt-32 pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-4">
            <Link to="/okresy" className="hover:underline">Okresy</Link>{data.region ? ` · ${(data.region || "").replace(/\s*kraj$/i, "")} kraj` : ""}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-none mb-6" data-testid="okres-h1">
            Zateplenie fasád <span className="text-gold">okres {data.name}</span>
          </h1>
          <p className="text-white/60 max-w-3xl mb-8">
            Zateplenie rodinných domov, bytových domov aj budov v okrese {data.name}. Pokrývame {data.town_count} obcí
            vrátane {data.towns.slice(0, 3).map((t) => t.name).join(", ")} a okolia. Cena na kľúč vrátane materiálu,
            práce, lešenia a dopravy — spočítajte si ju online do 60 sekúnd.
          </p>
          <div className="flex flex-wrap gap-3 mb-16">
            <span className="border border-white/15 px-4 py-2 text-sm text-white/70 font-mono2" data-testid="okres-stat-towns">{data.town_count} obcí v okrese</span>
            <span className="border border-white/15 px-4 py-2 text-sm text-white/70 font-mono2" data-testid="okres-stat-calcs">{data.calcs_count} online výpočtov z okresu</span>
            <span className="border border-white/15 px-4 py-2 text-sm text-white/70 font-mono2">Záruka 36/60 mesiacov</span>
          </div>

          {data.prices.length > 0 && (
            <section className="mb-16" data-testid="okres-prices">
              <h2 className="font-display text-3xl uppercase mb-2">Orientačné ceny v okrese {data.name}</h2>
              <p className="text-white/50 text-sm mb-6">Príklad pre rodinný dom s plochou fasády 140 m² — cena na kľúč vrátane DPH.</p>
              <div className="grid md:grid-cols-3 gap-4">
                {data.prices.map((p) => (
                  <div key={p.name} className={`border p-6 ${p.name === "Odporúčané" ? "border-gold bg-gold/5" : "border-white/10"}`} data-testid={`okres-price-${p.name}`}>
                    <div className="font-mono2 text-xs uppercase tracking-widest text-gold mb-2">{p.name}</div>
                    <div className="font-display text-4xl mb-1">{fmt(p.price_per_m2)} €/m²</div>
                    <div className="text-white/50 text-sm mb-4">spolu ≈ {fmt(p.total)} € · hrúbka {p.thickness} mm</div>
                    <div className="text-white/40 text-xs">Materiál, práca, lešenie, doprava a omietka v cene.</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {!IS_BOT && tiles && townsWithGps.length > 0 && (
            <section className="mb-16" data-testid="okres-map">
              <h2 className="font-display text-3xl uppercase mb-6">Mapa obcí v okrese {data.name}</h2>
              <div className="border border-white/10 overflow-hidden" style={{ height: "460px" }}>
                <MapContainer center={center} zoom={10} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#0A0A0A" }}>
                  <TileLayer attribution={tiles.attribution} url={tiles.url} />
                  {townsWithGps.map((t) => (
                    <Marker key={t.slug} position={[t.lat, t.lng]} icon={townIcon}>
                      <Popup>
                        <div style={{ fontFamily: "Manrope, Arial, sans-serif", fontSize: "13px" }}>
                          <strong>{t.name}</strong>
                          <div><a href={`/zateplenie-fasady/${t.slug}`}>Cena zateplenia – {t.name}</a></div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
            </section>
          )}

          <section className="mb-16" data-testid="okres-towns">
            <h2 className="font-display text-3xl uppercase mb-6">Obce v okrese {data.name}</h2>
            <div className="flex flex-wrap gap-2">
              {data.towns.map((t) => (
                <Link key={t.slug} to={`/zateplenie-fasady/${t.slug}`} data-testid={`okres-town-${t.slug}`}
                  className="border border-white/10 px-3 py-1.5 text-sm text-white/60 hover:border-gold hover:text-gold transition-colors">
                  {t.name}
                </Link>
              ))}
            </div>
          </section>

          {data.firms.length > 0 && (
            <section className="mb-16" data-testid="okres-firms">
              <h2 className="font-display text-3xl uppercase mb-6">Realizačné firmy v okrese {data.name}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.firms.map((f) => (
                  <div key={f.slug || f.company} className="border border-white/10 p-5">
                    <div className="flex items-center gap-2 text-white font-semibold mb-1"><Building2 className="w-4 h-4 text-gold" /> {f.company}</div>
                    <div className="text-white/40 text-xs mb-2">{f.city}</div>
                    {f.bio && <p className="text-white/50 text-sm">{f.bio.slice(0, 140)}{f.bio.length > 140 ? "…" : ""}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="mb-16" data-testid="okres-why">
            <h2 className="font-display text-3xl uppercase mb-6">Prečo zatepliť s nami</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {["Cena na kľúč bez skrytých poplatkov", "Overení partneri z regiónu", "Záruka 36 mes. na prácu, 60 mes. na materiál", "Online kalkulácia do 60 sekúnd"].map((t) => (
                <div key={t} className="border border-white/10 p-5 flex gap-3">
                  <Check className="w-5 h-5 text-gold shrink-0" />
                  <div className="text-white/70 text-sm">{t}</div>
                </div>
              ))}
            </div>
          </section>

          <Calculator context={`okres-${slug}`} />
        </div>
      </main>
      <Footer />
      <StickyCTA />
    </div>
  );
}
